import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Calendar, Clock, Video, HouseSimple, UsersThree, Sparkle, ArrowRight, MapPin, MagnifyingGlass, CheckCircle, Star,
} from '@phosphor-icons/react';
import Header from '@/components/Header';
import PremiumFooter from '@/components/PremiumFooter';
import MessagingInbox from '@/components/wellness/MessagingInbox';
import { formatPrice, MODES } from '@/data/wellness';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MODE_ICON = { online: Video, in_person: UsersThree, home_visit: HouseSimple };

const MyWellnessPage = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(user?.email || searchParams.get('email') || '');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchBookings = useCallback(async (queryEmail) => {
    if (!queryEmail) return;
    setLoading(true);
    setHasSearched(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/wellness/my-bookings`, { params: { email: queryEmail } });
      setBookings(res.data);
    } catch (err) {
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (email) fetchBookings(email);
  }, [email, fetchBookings]);

  const upcoming = bookings.filter((b) => new Date(b.scheduled_at) >= new Date() && b.status === 'confirmed');
  const past = bookings.filter((b) => new Date(b.scheduled_at) < new Date() || b.status !== 'confirmed');

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="my-wellness-page">
      <Header user={user} />

      <section className="max-w-6xl mx-auto px-6 pt-10 pb-16">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C05C3D] mb-3">My Wellness</p>
        <h1 className="text-4xl md:text-5xl font-semibold text-[#1C1917] font-heading tracking-tight mb-3">
          {user?.name ? `Hello, ${user.name.split(' ')[0]}` : 'Your wellness journey'}
        </h1>
        <p className="text-stone-600 mb-8 leading-relaxed">Track your bookings, upcoming sessions, and Nova Wellness activity in one place.</p>

        {!user && (
          <div className="bg-white border border-stone-100 rounded-3xl p-5 mb-8 flex items-center gap-3">
            <MagnifyingGlass size={18} weight="bold" className="text-stone-400" />
            <input
              type="email"
              placeholder="Enter your email to view bookings"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchBookings(email)}
              className="flex-1 outline-none text-sm bg-transparent"
              data-testid="mywellness-email-input"
            />
            <button onClick={() => fetchBookings(email)} className="bg-[#1A4331] text-white px-5 py-2 rounded-full text-sm font-semibold hover:bg-[#2A5A44] transition-all" data-testid="mywellness-search-btn">
              View
            </button>
          </div>
        )}

        {loading ? (
          <div className="grid gap-4">
            {[...Array(3)].map((_, i) => <div key={i} className="h-32 bg-white rounded-3xl border border-stone-100 animate-pulse" />)}
          </div>
        ) : bookings.length === 0 && hasSearched ? (
          <div className="bg-white rounded-3xl border border-stone-100 p-8 md:p-14 text-center" data-testid="mywellness-empty">
            <div className="w-20 h-20 mx-auto rounded-3xl bg-gradient-to-br from-[#F0F7F1] to-[#E8F5E9] flex items-center justify-center mb-6">
              <Sparkle size={32} weight="duotone" className="text-[#1A4331]" />
            </div>
            <h3 className="text-2xl font-semibold text-[#1C1917] font-heading tracking-tight mb-2">Your wellness calendar is clear</h3>
            <p className="text-stone-600 leading-relaxed max-w-lg mx-auto mb-6">Book your first session with a Nova Wellness expert.</p>
            <button onClick={() => navigate('/wellness/find')} className="bg-[#1A4331] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#2A5A44] transition-all inline-flex items-center gap-2 shadow-md">
              Find an Expert <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        ) : bookings.length > 0 ? (
          <div className="space-y-10">
            {/* Overview stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total', value: bookings.length },
                { label: 'Upcoming', value: upcoming.length },
                { label: 'Completed', value: bookings.filter((b) => b.status === 'completed').length },
                { label: 'Professionals', value: new Set(bookings.map((b) => b.professional_id)).size },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-2xl p-4 border border-stone-100">
                  <p className="text-xs uppercase tracking-[0.14em] text-stone-500 font-semibold">{s.label}</p>
                  <p className="text-2xl font-semibold text-[#1A4331] font-heading tracking-tight">{s.value}</p>
                </div>
              ))}
            </div>

            {/* Messages */}
            <div>
              <h2 className="text-xs font-bold uppercase tracking-[0.16em] text-stone-500 mb-4">Messages</h2>
              <MessagingInbox role="customer" customerEmail={email} />
            </div>

            {upcoming.length > 0 && (
              <div>
                <h2 className="text-xs font-bold uppercase tracking-[0.16em] text-stone-500 mb-4">Upcoming Sessions</h2>
                <div className="grid gap-3">
                  {upcoming.map((b) => <BookingCard key={b.booking_id} b={b} />)}
                </div>
              </div>
            )}

            {past.length > 0 && (
              <div>
                <h2 className="text-xs font-bold uppercase tracking-[0.16em] text-stone-500 mb-4">Past &amp; Cancelled</h2>
                <div className="grid gap-3">
                  {past.map((b) => <BookingCard key={b.booking_id} b={b} muted />)}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-stone-100 p-8 text-center">
            <p className="text-stone-500">Enter your email above to view your bookings.</p>
          </div>
        )}
      </section>

      <PremiumFooter />
    </div>
  );
};

const BookingCard = ({ b, muted }) => {
  const [reviewing, setReviewing] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: '', text: '' });
  const [submitted, setSubmitted] = useState(false);
  const M = MODE_ICON[b.mode];
  const when = new Date(b.scheduled_at);
  const dateStr = when.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const timeStr = when.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  const submitReview = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/wellness/bookings/${b.booking_id}/review`, {
        customer_email: b.customer_email,
        rating: reviewForm.rating,
        title: reviewForm.title,
        text: reviewForm.text,
      });
      setSubmitted(true);
      setReviewing(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit review');
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`bg-white rounded-2xl border border-stone-100 p-5 ${muted ? 'opacity-70' : ''}`} data-testid={`booking-${b.booking_id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <p className="font-semibold text-[#1C1917]">{b.professional_name}</p>
            <span className={`text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full ${b.status === 'confirmed' ? 'bg-emerald-50 text-emerald-700' : b.status === 'completed' ? 'bg-blue-50 text-blue-700' : b.status === 'cancelled' ? 'bg-red-50 text-red-700' : 'bg-stone-100 text-stone-600'}`}>{b.status}</span>
          </div>
          <p className="text-sm text-stone-500">{b.service_name}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-stone-600">
            <span className="inline-flex items-center gap-1"><Calendar size={12} weight="fill" /> {dateStr}</span>
            <span className="inline-flex items-center gap-1"><Clock size={12} weight="fill" /> {timeStr}</span>
            {M && <span className="inline-flex items-center gap-1"><M size={12} weight="duotone" /> {MODES.find((x) => x.id === b.mode)?.label}</span>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-stone-400">Amount</p>
            <p className="font-semibold text-[#1A4331]">{formatPrice(b.gross_amount, b.currency)}</p>
          </div>
          {b.meeting_url && b.status === 'confirmed' && (
            <a href={b.meeting_url} target="_blank" rel="noreferrer" className="bg-[#1A4331] text-white px-4 py-2 rounded-full text-xs font-bold hover:bg-[#2A5A44] transition-all inline-flex items-center gap-1">
              <Video size={12} weight="duotone" /> Join
            </a>
          )}
          {b.status === 'completed' && !submitted && !reviewing && (
            <button onClick={() => setReviewing(true)} className="bg-amber-500 text-white px-4 py-2 rounded-full text-xs font-bold hover:bg-amber-600 inline-flex items-center gap-1" data-testid={`review-btn-${b.booking_id}`}>
              <Star size={12} weight="fill" /> Leave Review
            </button>
          )}
          {submitted && <span className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Reviewed</span>}
        </div>
      </div>

      {reviewing && (
        <div className="mt-4 bg-stone-50 rounded-2xl p-4 space-y-2" data-testid={`review-form-${b.booking_id}`}>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => setReviewForm({ ...reviewForm, rating: n })} className="p-1" data-testid={`review-star-${b.booking_id}-${n}`}>
                <Star size={20} weight="fill" className={n <= reviewForm.rating ? 'text-amber-500' : 'text-stone-300'} />
              </button>
            ))}
            <span className="ml-2 text-sm text-stone-600">{reviewForm.rating}/5</span>
          </div>
          <input placeholder="Title (optional)" value={reviewForm.title} onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })} className="w-full bg-white border border-stone-200 rounded-xl px-3 py-2 text-sm" />
          <textarea rows={2} placeholder="Share your experience..." value={reviewForm.text} onChange={(e) => setReviewForm({ ...reviewForm, text: e.target.value })} className="w-full bg-white border border-stone-200 rounded-xl px-3 py-2 text-sm resize-none" data-testid={`review-text-${b.booking_id}`} />
          <div className="flex gap-2">
            <button onClick={submitReview} className="bg-[#1A4331] text-white px-4 py-2 rounded-full text-xs font-bold hover:bg-[#2A5A44]" data-testid={`review-submit-${b.booking_id}`}>Submit Review</button>
            <button onClick={() => setReviewing(false)} className="bg-white border border-stone-200 text-stone-600 px-4 py-2 rounded-full text-xs font-semibold">Cancel</button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default MyWellnessPage;
